<script setup lang="ts">
import dayjs from 'dayjs'

import { getCampaignPreviewText } from '../utils/campaignMessage'

import type { LoyaltyReward, Campaign } from '@/types/types'
import { getRowContentCreator, getRowContentScope } from '@/utils/contentScope'

const { getStoresByPermission } = useStore()

const router = useRouter()
const { formatNumber, formatPercentage, formatCurrency } = useNumber()
const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    campaigns: Array<Campaign>
    pagination: {
      currentPage: number
      lastPage: number
      total: number
      pageSize: number
    }
    isFetching: boolean
  }>(),
  {
    pagination: () => ({
      currentPage: 1,
      lastPage: 1,
      total: 0,
      pageSize: 15
    })
  }
)

const emit = defineEmits(['update:changePage'])

const storesWithPermission = computed(() => getStoresByPermission('campaign.read'))

const goToDetails = (campaign) => {
  router.push({ name: 'CampaignDetail', params: { campaignId: campaign.id } })
}

const isLaunched = (date) => {
  return dayjs(date) <= dayjs()
}

const changePage = (page) => {
  emit('update:changePage', page)
}

const hasCampaignStores = computed(() => {
  return props.campaigns.some((campaign) => campaign.stores?.length > 0)
})

const columns = {
  name: {
    label: t('pages.campaign.campaignPage.table.thead.name')
  },
  status: {
    label: t('pages.campaign.campaignPage.table.thead.status')
  },
  sendAt: {
    label: t('pages.campaign.campaignPage.table.thead.sendAt')
  },
  origin: {
    label: t('contentScope.origin')
  },
  stores: {
    label: t('pages.campaign.campaignPage.table.thead.stores')
  },
  clickRate: {
    label: t('pages.campaign.campaignPage.table.thead.clicksRate'),
    numeric: true
  },
  conversions: {
    label: t('pages.campaign.campaignPage.table.thead.conversions'),
    numeric: true
  },
  ca: {
    label: t('pages.campaign.campaignPage.table.thead.ca'),
    numeric: true
  },
  creator: {
    label: t('contentScope.filters.createdBy')
  }
}

const table = computed(() => {
  let columnsObj = { ...columns }

  if (!hasCampaignStores.value) delete columnsObj.stores

  return columnsObj
})

const rows = computed(() => {
  return props.campaigns.map((campaign) => {
    const store = {
      id: campaign.id,
      data: {
        name: {
          name: campaign.name,
          description: getCampaignPreviewText(
            campaign.messageContent,
            campaign.stores?.[0]?.name ?? '',
            campaign?.reward as unknown as LoyaltyReward
          )
        },
        scope: campaign.scope ?? null,
        creator: campaign.creator ?? null,
        status: isLaunched(campaign.sentAt),
        sendAt: {
          date: dayjs(campaign.sentAt).format('DD/MM/YYYY'),
          hour: dayjs(campaign.sentAt).format('HH:mm:ss')
        },
        stores: campaign.stores,
        visitCount: campaign.statistics?.visitsCount || '-',
        visits: campaign.statistics?.visitsPercentage
          ? formatPercentage(campaign.statistics?.visitsPercentage, campaign.statistics?.visitsPercentage < 1 ? 2 : 1)
          : null,
        clickCount: campaign.statistics?.clicksCount || '-',
        clicks: campaign.statistics?.clicksPercentage
          ? formatPercentage(campaign.statistics?.clicksPercentage, campaign.statistics?.clicksPercentage < 1 ? 2 : 1)
          : null,
        ca: campaign.statistics?.generatedRevenue ? formatCurrency(campaign.statistics?.generatedRevenue) : '-',
        conversionDelay: campaign.conversionDelay,
        hasQrcode: campaign.hasQrcode,
        statistics: campaign.statistics
      }
    }

    if (!hasCampaignStores.value) delete store.data.stores

    return store
  })
})
</script>

<template>
  <div>
    <AppTable
      :columns="table"
      :rows="rows"
      :pagination="pagination"
      :is-loading="isFetching"
      row-classes="cursor-pointer"
      @update:change-page="changePage($event)"
      @row-clicked="goToDetails($event)"
    >
      <template #th-clickRate="{ th }">
        <p class="min-w-32 flex flex-row-reverse items-center gap-2 leading-[20px]">
          <span>{{ th.label }}</span>
          <BaseIcon name="HeroArrowPathIconSolid" class="min-h-5 w-5 min-w-5 text-blue-600" />
        </p>
      </template>

      <template #th-conversions="{ th }">
        <p class="min-w-32 flex flex-row-reverse items-center gap-2 leading-[20px]">
          <span>{{ th.label }}</span>
          <BaseIcon name="HeroArrowPathIconSolid" class="min-h-5 w-5 min-w-5 text-blue-600" />
        </p>
      </template>

      <template #th-ca="{ th }">
        <p class="min-w-32 flex flex-row-reverse items-center gap-2 leading-[20px]">
          <span>{{ th.label }}</span>
          <BaseIcon name="HeroArrowPathIconSolid" class="min-h-5 w-5 min-w-5 text-blue-600" />
        </p>
      </template>

      <template #td-name="{ data }">
        <div class="flex min-w-[320px] max-w-[560px] flex-col">
          <p
            class="max-w-[40ch] truncate text-sm font-medium leading-5 text-foreground"
            :title="data.name.name || undefined"
          >
            {{ data.name.name || '-' }}
          </p>
          <p class="mt-1 truncate text-sm text-muted-foreground">
            {{ data.name.description || '-' }}
          </p>
        </div>
      </template>

      <template #th-origin>
        <ContentScopeColumnHeader subject="campaign" />
      </template>
      <template #td-origin="{ data }">
        <ContentScopeBadge :scope="getRowContentScope(data)" />
      </template>

      <template #td-creator="{ data }">
        <ContentScopeCreator :creator="getRowContentCreator(data)" />
      </template>

      <template #td-status="{ data }">
        <BaseChip
          :text="
            data.status
              ? t('pages.campaign.campaignPage.items.status.sent')
              : t('pages.campaign.campaignPage.items.status.scheduled')
          "
          :color="data.status ? 'green' : 'yellow'"
        />
      </template>

      <template #td-sendAt="{ data }">
        <div>
          <p class="text-sm font-medium leading-5 text-foreground">
            {{ data.sendAt.date }}
          </p>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ data.sendAt.hour }}
          </p>
        </div>
      </template>

      <template v-if="hasCampaignStores" #td-stores="{ data }">
        <StoreTooltip
          v-if="data.stores.length > 0"
          :stores="data.stores"
          :available-store-ids="storesWithPermission.map((store) => store.id)"
        />
        <span v-else>-</span>
      </template>

      <template #td-conversions="{ data }">
        <p>{{ formatNumber(data.visitCount) }}</p>
        <p v-if="data.visits" class="mt-1 text-xs font-normal text-muted-foreground">
          {{ data.visits }}
        </p>
      </template>

      <template #td-clickRate="{ data }">
        <p>{{ formatNumber(data.clickCount) }}</p>
        <p v-if="data.clicks" class="mt-1 text-xs font-normal text-muted-foreground">
          {{ data.clicks }}
        </p>
      </template>
    </AppTable>
  </div>
</template>
